import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const languageConfig: Record<string, { flag: string; nativeLabel: string }> = {
  en: { flag: '🇺🇸', nativeLabel: 'English' },
  'zh-TW': { flag: '🇹🇼', nativeLabel: '繁體中文' },
  vi: { flag: '🇻🇳', nativeLabel: 'Tiếng Việt' },
  fil: { flag: '🇵🇭', nativeLabel: 'Filipino' },
  id: { flag: '🇮🇩', nativeLabel: 'Bahasa Indonesia' },
  ms: { flag: '🇲🇾', nativeLabel: 'Bahasa Melayu' },
};

const availableLanguages = ['en', 'zh-TW', 'vi', 'fil', 'id', 'ms'];

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = languageConfig[i18n.language] || languageConfig['en'];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1.5"
        aria-label="Select language"
      >
        <span className="text-sm">{currentLang.flag}</span>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {currentLang.nativeLabel}
        </span>
        <svg
          className={`w-3 h-3 text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 py-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg z-50">
          {availableLanguages.map((lang) => {
            const config = languageConfig[lang];
            const isActive = lang === i18n.language;
            return (
              <button
                key={lang}
                onClick={() => {
                  i18n.changeLanguage(lang);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  isActive
                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <span>{config.flag}</span>
                <span className="flex-1">{config.nativeLabel}</span>
                {isActive && (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
